/**
 * Mirror of the backend's SCOPES catalog (src/common/scopes/scope-catalog.ts).
 * Kept in sync by hand — the list evolves slowly, and shipping the backend's
 * TS as a package just to share nine strings would be heavier than this.
 */

export const SCOPES = [
  'card:read',
  'card:write',
  'card:secrets',
  'program:read',
  'program:write',
  'customer:read',
  'customer:write',
  'account:read',
  'webhook:read',
  'webhook:write',
  '*',
] as const;

export type Scope = (typeof SCOPES)[number];

export interface ScopeGroup {
  label: string;
  description: string;
  scopes: { scope: Exclude<Scope, '*'>; label: string; detail: string }[];
}

/**
 * Picker layout. `card:secrets` is kept in its own line (not next to
 * card:read/write) because its risk profile is different enough that pairing
 * it visually with the other card scopes would be misleading.
 */
export const SCOPE_GROUPS: ScopeGroup[] = [
  {
    label: 'Cards',
    description: 'Read, create, freeze. Pulling PAN/CVV is a separate scope.',
    scopes: [
      { scope: 'card:read', label: 'Read', detail: 'List and view cards.' },
      {
        scope: 'card:write',
        label: 'Write',
        detail: 'Issue, update, freeze, unfreeze.',
      },
      {
        scope: 'card:secrets',
        label: 'PAN & CVV',
        detail:
          'Fetch secure card data via the ephemeral viewer. Grant sparingly.',
      },
    ],
  },
  {
    label: 'Programs',
    description: 'Program metadata, funding mode, webhooks.',
    scopes: [
      { scope: 'program:read', label: 'Read', detail: 'List and view programs.' },
      { scope: 'program:write', label: 'Write', detail: 'Create and update.' },
    ],
  },
  {
    label: 'Customers',
    description: 'Cardholder records (identity + status).',
    scopes: [
      { scope: 'customer:read', label: 'Read', detail: 'List and view customers.' },
      { scope: 'customer:write', label: 'Write', detail: 'Create and update.' },
    ],
  },
  {
    label: 'Accounts',
    description: 'Wallet balances and ledger entries.',
    scopes: [
      { scope: 'account:read', label: 'Read', detail: 'Balance and history.' },
    ],
  },
  {
    label: 'Webhooks',
    description: 'Delivery destinations and signing secrets.',
    scopes: [
      { scope: 'webhook:read', label: 'Read', detail: 'List endpoints.' },
      { scope: 'webhook:write', label: 'Write', detail: 'Create, update, rotate.' },
    ],
  },
];

/**
 * Loose CIDR validation that matches the backend's `isCidr` closely enough
 * for a form field. Accepts IPv4 `a.b.c.d/N` with 0 ≤ N ≤ 32 and bare
 * `a.b.c.d` (treated as `/32`); same for IPv6 with `/0..128`. The backend
 * validates authoritatively; this just prevents an obvious typo from making
 * the round trip.
 */
export function isCidrLike(input: string): boolean {
  const v = input.trim();
  if (v === '') return false;
  if (/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(v)) {
    const [addr, bits] = v.split('/') as [string, string | undefined];
    const octets = addr.split('.').map(Number);
    if (octets.some((o) => o < 0 || o > 255)) return false;
    const n = bits === undefined ? 32 : Number(bits);
    return n >= 0 && n <= 32;
  }
  // Minimal IPv6 shape check — hex blocks + optional ::, optional /0..128.
  if (/^[0-9a-fA-F:]+(\/\d{1,3})?$/.test(v) && v.includes(':')) {
    const [, bits] = v.split('/');
    const n = bits === undefined ? 128 : Number(bits);
    return n >= 0 && n <= 128;
  }
  return false;
}
